import { useState } from 'react';
import Header from '../components/Header.jsx';
import Footer from '../components/Footer.jsx';
import { supabase } from '../services/supabaseClient.js';

const SUBJECTS = ['Question', 'Affiliate Inquiry', 'Partnership', 'Press', 'Bug Report', 'Other'];
const SUPPORT_EMAIL = 'info@adaptos.app';
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function ContactPage() {
  const [form, setForm]     = useState({ name: '', email: '', phone: '', subject: 'Question', message: '' });
  const [loading, setLoading]     = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError]         = useState('');

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    if (!form.name.trim()) return 'Please enter your name.';
    if (!EMAIL_RE.test(form.email.trim())) return 'Please enter a valid email address.';
    if (form.message.trim().length < 10) return 'Your message is a little short — tell us a bit more.';
    if (form.message.trim().length > 5000) return 'Your message is too long (5000 characters max).';
    return '';
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    const v = validate();
    if (v) { setError(v); return; }
    setError('');
    setLoading(true);
    try {
      const { error: insErr } = await supabase
        .from('contact_submissions')
        .insert({
          name:       form.name.trim(),
          email:      form.email.trim().toLowerCase(),
          phone:      form.phone?.trim() || null,
          subject:    form.subject,
          message:    form.message.trim(),
          user_agent: navigator.userAgent.slice(0, 500),
        });
      if (insErr) throw insErr;
      setSubmitted(true);
    } catch (err) {
      console.error('Contact submission failed:', err);
      setError('Could not send your message. Please try again in a moment.');
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setForm({ name: '', email: '', phone: '', subject: 'Question', message: '' });
    setSubmitted(false);
    setError('');
  };

  return (
    <div className="cp-root">
      <div className="cp-glow" aria-hidden="true" />
      <Header />

      <main className="cp-main">
        <div className="cp-card">
          {submitted ? (
            <div className="cp-success">
              <div className="cp-check" aria-hidden="true">✓</div>
              <h1 className="cp-h1">Thanks — we got your message.</h1>
              <p className="cp-sub">Expect a reply within 2 business days.</p>
              <button className="cp-link-btn" onClick={reset}>Send another</button>
            </div>
          ) : (
            <>
              <h1 className="cp-h1">Get in touch</h1>
              <p className="cp-sub">
                Questions, partnership inquiries, or just saying hi — we read every message.
              </p>

              <form className="cp-form" onSubmit={handleSubmit} noValidate>
                <div className="cp-row">
                  <label className="cp-field">
                    <span className="cp-label">Name <span className="cp-req">*</span></span>
                    <input className="cp-input" type="text" value={form.name} onChange={set('name')} placeholder="Your name" />
                  </label>
                  <label className="cp-field">
                    <span className="cp-label">Email <span className="cp-req">*</span></span>
                    <input className="cp-input" type="email" value={form.email} onChange={set('email')} placeholder="you@example.com" />
                  </label>
                </div>

                <div className="cp-row">
                  <label className="cp-field">
                    <span className="cp-label">Phone <span className="cp-opt">(optional)</span></span>
                    <input className="cp-input" type="tel" value={form.phone} onChange={set('phone')} placeholder="(555) 555-5555" />
                  </label>
                  <label className="cp-field">
                    <span className="cp-label">Subject <span className="cp-req">*</span></span>
                    <select className="cp-input cp-select" value={form.subject} onChange={set('subject')}>
                      {SUBJECTS.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </label>
                </div>

                <label className="cp-field">
                  <span className="cp-label">Message <span className="cp-req">*</span></span>
                  <textarea className="cp-input cp-textarea" value={form.message} onChange={set('message')} rows={6} maxLength={5000} placeholder="How can we help?" />
                  <span className="cp-count">{form.message.trim().length}/5000</span>
                </label>

                {error && <div className="cp-error">{error}</div>}

                <button className="cp-submit" type="submit" disabled={loading}>
                  {loading ? 'Sending…' : 'Send message'}
                </button>
              </form>

              <p className="cp-note">
                For urgent technical issues, you can also reach support at{' '}
                <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
              </p>
            </>
          )}
        </div>
      </main>

      <Footer />

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,300..600&family=Hanken+Grotesk:wght@300;400;500;600;700&display=swap');

        .cp-root {
          background: #0f0f12; color: #f4f4f5; min-height: 100vh;
          position: relative; overflow-x: hidden;
          font-family: 'Hanken Grotesk', sans-serif;
          display: flex; flex-direction: column;
        }
        .cp-glow {
          position: fixed; top: -22%; left: 50%; transform: translateX(-50%);
          width: min(900px, 120vw); height: 640px;
          background: radial-gradient(ellipse at center, rgba(45,212,191,0.14) 0%, transparent 65%);
          filter: blur(20px); pointer-events: none; z-index: 0;
        }
        .cp-main {
          position: relative; z-index: 1; flex: 1;
          display: flex; justify-content: center; align-items: flex-start;
          padding: clamp(32px, 6vh, 72px) clamp(16px, 4vw, 40px) clamp(48px, 8vh, 88px);
        }
        .cp-card {
          width: 100%; max-width: 620px;
          background: rgba(255,255,255,0.025); border: 1px solid rgba(255,255,255,0.08);
          border-radius: 20px; padding: clamp(28px, 5vw, 44px);
        }
        .cp-h1 {
          font-family: 'Fraunces', serif; font-weight: 500;
          font-size: clamp(28px, 4.5vw, 42px); line-height: 1.1;
          letter-spacing: -0.02em; margin: 0;
        }
        .cp-sub { font-weight: 300; color: rgba(244,244,245,0.62); font-size: clamp(15px, 2vw, 18px); line-height: 1.5; margin: 14px 0 0; }
        .cp-form { display: flex; flex-direction: column; gap: 16px; margin-top: 26px; }
        .cp-row { display: flex; gap: 16px; }
        .cp-field { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 6px; position: relative; }
        .cp-label { font-size: 12px; font-weight: 700; color: rgba(244,244,245,0.7); text-transform: uppercase; letter-spacing: .05em; }
        .cp-req { color: #2dd4bf; }
        .cp-opt { color: rgba(244,244,245,0.4); font-weight: 400; text-transform: none; letter-spacing: 0; }
        .cp-input {
          width: 100%; box-sizing: border-box;
          background: #15151b; border: 1px solid rgba(255,255,255,0.10); border-radius: 11px;
          padding: 13px 14px; color: #f4f4f5; font-size: 15px; font-family: inherit; outline: none;
          transition: border-color .18s ease;
        }
        .cp-input::placeholder { color: #6b6760; }
        .cp-input:focus { border-color: #2dd4bf; }
        .cp-select { appearance: none; cursor: pointer; }
        .cp-textarea { resize: vertical; min-height: 120px; line-height: 1.55; }
        .cp-count { position: absolute; right: 2px; bottom: -18px; font-size: 11px; color: rgba(244,244,245,0.4); }
        .cp-error {
          background: rgba(248,113,113,0.10); border: 1px solid rgba(248,113,113,0.30);
          color: #fca5a5; border-radius: 10px; padding: 11px 14px; font-size: 13.5px; line-height: 1.5;
        }
        .cp-submit {
          margin-top: 8px; background: linear-gradient(135deg, #2dd4bf, #14b8a6); color: #0f0f12;
          border: none; border-radius: 12px; padding: 15px 28px; font-size: 16px; font-weight: 700;
          cursor: pointer; font-family: inherit; box-shadow: 0 6px 22px rgba(45,212,191,0.30);
          transition: transform .18s ease, box-shadow .18s ease, opacity .18s ease;
        }
        .cp-submit:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 10px 30px rgba(45,212,191,0.42); }
        .cp-submit:disabled { opacity: .6; cursor: default; }
        .cp-note { margin: 22px 0 0; font-size: 13px; color: rgba(244,244,245,0.5); line-height: 1.55; }
        .cp-note a { color: #2dd4bf; text-decoration: none; }
        .cp-note a:hover { text-decoration: underline; }
        .cp-success { text-align: center; padding: 20px 0; }
        .cp-check {
          width: 56px; height: 56px; border-radius: 50%; margin: 0 auto 18px;
          display: flex; align-items: center; justify-content: center;
          background: rgba(52,211,153,0.15); color: #34d399; font-size: 26px; font-weight: 800;
        }
        .cp-link-btn {
          margin-top: 22px; background: transparent; border: 1.5px solid rgba(255,255,255,0.22);
          color: #f4f4f5; border-radius: 11px; padding: 12px 24px; font-size: 15px; font-weight: 600;
          cursor: pointer; font-family: inherit; transition: border-color .18s ease, color .18s ease;
        }
        .cp-link-btn:hover { border-color: #2dd4bf; color: #2dd4bf; }
        @media (max-width: 560px) { .cp-row { flex-direction: column; gap: 16px; } }
      `}</style>
    </div>
  );
}
